import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScannedActionsSubject, Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { AppUtilityService } from '../../services/app-utility.service';
import { StudyLinksService } from '../../services/study-links.service';
import { saveStudy, saveStudyError, saveStudySuccess, setComments, setProcessingStatus } from '../../../app/state/actions';
import { filter, take } from 'rxjs';
import { SaveDialogData } from '../../models/dialog.model';
import { SaveStudyRequest, Study } from '../../models/study.model';
import { selectAppState } from '../../../app/state/selectors';
import { getLatestAuditTrailComment } from '../../utils/audit-comment.util';
import { neighborsOf } from '../../utils/study-link.util';
import { isCalAdjustFinal } from '../../utils/run-type.util';

/**
 * SaveStudyDialogComponent
 * This component manages the dialog used for saving a study, either as a new study or updating an existing one. 
 * It handles the saving process, including dispatching actions, showing processing state, and updating study information.
 */
@Component({
  selector: 'app-save-study-dialog',
  templateUrl: './save-study-dialog.component.html',
  styleUrl: './save-study-dialog.component.scss'
})
export class SaveStudyDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<SaveStudyDialogComponent>);
  readonly data = inject<SaveDialogData>(MAT_DIALOG_DATA);
  readonly selectedStudy = this.data.study;

  appState$ = this.store.select(selectAppState);
  studyName = this.selectedStudy ? this.selectedStudy.studyName : '';
  panelName: string = '';
  panelPhase: string = '';
  testSpecPhase: string = '';
  operator: string = '';
  errorMessage!: string;
  isProcessing = false;
  calculationStatus: string = '';
  existingStudy = false;
  lastCalculationId: string | null = null;
  lastCalculationType: 'adjusted' | 'unadjusted' | null = null;
  lastUnadjustedCalculationId: string | null = null;
  lastAdjustedCalculationId: string | null = null;
  currentComments = '';
  lastSavedComment = '';
  lastAuditComment = '';
  commentInput = '';
  commentError = '';
  isCommitted = false;
  showLinkPicker = false;
  linkedRunNames: string[] = [];
  /**
   * Extra links picked for a Save-As. Kept apart from `linkedRunNames` and starting empty: the
   * new study does not inherit the source's links, or repeated Save-As would link every
   * generation to every other.
   */
  saveAsLinkedRunNames: string[] = [];
  /** False until the study's current linkages are established; the picker stays shut meanwhile. */
  linksKnown = false;
  /** True when the linkage list could not be fetched, so the picker cannot be trusted. */
  linksError = false;
  hasLoadedCalculationData = false;
  private commentEdited = false;
  /** True once the operator has changed the picker's selection. */
  private linksEdited = false;
  /** The name the study carried when the dialog opened; '' when it has never been saved. */
  readonly originalStudyName = (this.selectedStudy?.studyName ?? '').trim();
  /**
   * @param store NgRx Store for managing application state
   * @param appUtilityService Custom utility service
   * @param actions Subject to handle dispatched actions
   * @param studyLinksService Source of truth for the linkages between saved studies
   */
  constructor(
    private store: Store,
    private appUtilityService: AppUtilityService,
    private actions: ScannedActionsSubject,
    private studyLinksService: StudyLinksService,
  ) { }
  /**
     * ngOnInit lifecycle hook for setting up the initial state.
     * Subscribes to the app state and extracts relevant details such as panel name, phase, and user info.
     */
  ngOnInit() {
    this.appState$.pipe(
      filter(appState => !!appState)
    ).subscribe(appState => {
      this.panelName = appState.panelName;
      this.panelPhase = appState.selectedStudy.runType;
      this.testSpecPhase = appState.selectedStudy.testSpec;
      this.operator = appState.loggedInUser;
      this.calculationStatus = appState.calculationStatus;
      this.isProcessing = appState.isProcessing;
      this.existingStudy = appState.existingStudy;
      this.lastCalculationId = appState.lastCalculationId;
      this.lastCalculationType = appState.lastCalculationType;
      this.lastUnadjustedCalculationId = appState.lastUnadjustedCalculationId ?? null;
      this.lastAdjustedCalculationId = appState.lastAdjustedCalculationId ?? null;
      this.currentComments = appState.selectedStudy.comments ?? '';
      this.lastSavedComment = appState.lastSavedComment ?? '';
      this.lastAuditComment = getLatestAuditTrailComment(appState.selectedStudy.auditTrail ?? []);
      this.isCommitted = !!appState.selectedStudy.isCommitted;
      // Mirrors saveStudyEffect: a study with calculation data does a full save (studies/save/),
      // otherwise it saves as a draft (studies/save_draft/). Links only persist on full save.
      this.hasLoadedCalculationData = Boolean(
        appState.selectedStudy?.calculationResponse?.calculationResponse
        || appState.selectedStudy?.calculationAdjustedResponse?.calculationResponse
      );
      if (!this.commentEdited) {
        this.commentInput = this.currentComments;
      }
    });

    this.seedLinkedStudies();
  }

  /**
   * Establishes the study's current linkages from the linkage list rather than from the study
   * held in state.
   *
   * `selectedStudy.linkedStudies` is a snapshot taken when the study was loaded, and linkages
   * edited on the Studies screen go straight to the backend without passing through the store.
   * Seeding from it hands a stale set to a save that replaces every linkage the study has, which
   * silently drops the ones added since.
   */
  private seedLinkedStudies(): void {
    this.linksKnown = false;
    this.linksError = false;

    if (!this.originalStudyName) {
      // A study that has never been saved carries no linkages, so the set is known and empty.
      this.linksKnown = true;
      return;
    }

    this.studyLinksService.load().pipe(take(1)).subscribe({
      next: ({ links }) => {
        if (!this.linksEdited) {
          this.linkedRunNames = neighborsOf(links, this.originalStudyName);
        }
        this.linksKnown = true;
      },
      // Leaves linksKnown false so the picker stays shut and the save omits the field: without
      // the current set there is no way to replace it without deleting linkages never displayed.
      error: () => {
        this.linksError = true;
      }
    });
  }

  /**
   * Whether studies can be linked in this save. The backend only persists links on a full save;
   * a study with no calculation data saves as a draft, where links are ignored (MST-504).
   */
  get canLinkStudies(): boolean {
    const ids = this.resolveCalculationIds(this.selectedStudy ?? new Study(''));
    return this.hasLoadedCalculationData || Boolean(ids.unadjusted || ids.adjusted);
  }

  /**
   * True when this save is a Save-As — a saved study written out under a new name, which creates
   * a second study rather than updating the one that was loaded.
   *
   * Read live off the name field, so the dialog reflects the rename as the operator types it.
   */
  get isSaveAs(): boolean {
    const currentName = this.studyName.trim();
    return this.existingStudy
      && this.originalStudyName.length > 0
      && currentName.length > 0
      && currentName !== this.originalStudyName;
  }

  /** The one-line summary shown on the Linked Studies panel header. */
  get linkedStudiesSummary(): string {
    if (this.isSaveAs) {
      const extra = this.saveAsLinkedRunNames.filter(name => name !== this.originalStudyName).length;
      return extra
        ? `Linking to ${this.originalStudyName}, plus ${extra} more`
        : `Linking to ${this.originalStudyName}`;
    }
    if (!this.canLinkStudies) {
      return 'Available after calculations are run';
    }
    if (this.linksError) {
      return 'Existing links could not be loaded';
    }
    if (!this.linksKnown) {
      return 'Loading existing links…';
    }
    return this.linkedRunNames.length ? `${this.linkedRunNames.length} linked` : 'None';
  }

  /** Save the current study, updating when the name is unchanged and creating when renamed. */
  saveStudy() {
    if (!this.isProcessing) {
      if (!this.validateComments()) {
        return;
      }

      this.store.dispatch(setProcessingStatus({ isProcessing: true, activity: 'saving' }));
      this.errorMessage = '';
      let study: Study;

      if (this.selectedStudy) {
        study = cloneDeep(this.selectedStudy);
      } else {
        study = new Study('');
      }

      study.studyName = this.studyName;
      study.comments = this.commentInput.trim();
      this.store.dispatch(setComments({ comments: study.comments }));

      const calculationIds = this.resolveCalculationIds(study);
      const unadjustedCalculationId = calculationIds.unadjusted;
      const adjustedCalculationId = calculationIds.adjusted;

      this.actions.pipe(
        filter(action => action.type === saveStudySuccess.type
          || action.type === saveStudyError.type
        ),
        take(1)
      ).subscribe(() => {
        this.store.dispatch(setProcessingStatus({ isProcessing: false }));
      });

      const isSaveAs = this.isSaveAs;
      const updateExisting = this.existingStudy && !isSaveAs;

      // A Save-As lands on a run name that holds no calc responses of its own, so name the run to
      // copy them (and their sample data) forward from. Only when the unadjusted half was not
      // rerun — an adjusted-only id, or no ids at all. Sending it when a rerun did supply that half
      // makes the backend prefer the source's older copy over the fresh one.
      // A Final study has no unadjusted half, so any the source holds would be copied in stale.
      const isFinalWithAdjustedId = isCalAdjustFinal(study) && !!adjustedCalculationId;
      const sourceRunName = isSaveAs && !unadjustedCalculationId && !isFinalWithAdjustedId
        ? this.originalStudyName
        : undefined;

      const saveStudyRequest = new SaveStudyRequest(
        study,
        this.panelName,
        this.panelPhase,
        this.testSpecPhase,
        this.studyName,
        updateExisting,
        this.calculationStatus,
        unadjustedCalculationId,
        adjustedCalculationId,
        sourceRunName,
        this.linksToPersist(updateExisting, isSaveAs)
      );

      this.store.dispatch(saveStudy({
        saveStudyRequest: saveStudyRequest,
        // Only the full save honors related_study_names, so the effect adds this linkage itself
        // when the study saves as a draft.
        autoLinkToStudyName: isSaveAs ? this.originalStudyName : undefined
      }));

      this.dialogRef.close();

    }
  }

  /** The picker's selection: the Save-As picks while renaming, otherwise the study's own links. */
  get pickerLinkedRunNames(): string[] {
    return this.isSaveAs ? this.saveAsLinkedRunNames : this.linkedRunNames;
  }

  /** Keeps whichever list the picker is showing in sync with it. */
  onLinkedChange(linkedRunNames: string[]): void {
    if (this.isSaveAs) {
      this.saveAsLinkedRunNames = linkedRunNames;
      return;
    }
    this.linkedRunNames = linkedRunNames;
    this.linksEdited = true;
  }

  /**
   * The linkages to persist, or undefined to leave them untouched.
   *
   * A save replaces every linkage the study has, so an ordinary save only states them when it
   * knows the current set and the operator edited the picker. Otherwise it leaves the linkage
   * table alone, so a save made after linking studies elsewhere cannot undo that work.
   *
   * A Save-As links the new run back to its source plus anything picked for it — never the
   * source's own links, so a chain of Save-As stays a chain. The new run holds no links yet, so
   * stating the set deletes nothing and does not depend on the source's links being readable.
   */
  private linksToPersist(updateExisting: boolean, isSaveAs: boolean): string[] | undefined {
    if (isSaveAs) {
      return Array.from(new Set([...this.saveAsLinkedRunNames, this.originalStudyName]));
    }
    if (!this.linksKnown) {
      return undefined;
    }
    return this.linksEdited || !updateExisting ? this.linkedRunNames : undefined;
  }

  handleCommentInputChange(value: string): void {
    this.commentEdited = true;
    this.commentInput = value;
    this.commentError = '';
  }

  private normalizeComment(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  private validateComments(): boolean {
    const normalizedNew = this.normalizeComment(this.commentInput);
    const normalizedPrev = this.normalizeComment(this.lastAuditComment || this.lastSavedComment);

    if (!normalizedNew.length) {
      this.commentError = 'Please enter comments before saving this study.';
      return false;
    }

    if (normalizedNew === normalizedPrev) {
      this.commentError = 'Comment must differ from the previous saved comment.';
      return false;
    }

    this.commentError = '';
    return true;
  }

  private resolveCalculationIds(study: Study): { unadjusted?: string; adjusted?: string } {
    const storedUnadjusted = study.unadjustedCalculationId ?? null;
    const storedAdjusted = study.adjustedCalculationId ?? null;

    let unadjusted: string | null = null;
    let adjusted: string | null = null;

    if (this.lastCalculationType === 'adjusted') {
      unadjusted = this.lastUnadjustedCalculationId ?? storedUnadjusted;
      adjusted = this.lastCalculationId ?? this.lastAdjustedCalculationId ?? storedAdjusted;
    } else if (this.lastCalculationType === 'unadjusted') {
      unadjusted = this.lastCalculationId ?? this.lastUnadjustedCalculationId ?? storedUnadjusted;
      adjusted = this.lastAdjustedCalculationId ?? storedAdjusted;
    } else {
      unadjusted = this.lastUnadjustedCalculationId ?? storedUnadjusted;
      adjusted = this.lastAdjustedCalculationId ?? storedAdjusted;
    }

    return {
      // A Final run replaces the baseline, so no unadjusted half is saved with it.
      unadjusted: isCalAdjustFinal(study) ? undefined : unadjusted ?? undefined,
      adjusted: adjusted ?? undefined
    };
  }
}
